import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { apiAxios } from '@/api'
import { fetchWrapper } from '@/reducers/fetch'
import { parseErrorCodeFromCatch } from '@/utils/errorHelpers'
import type { ReduxState } from '@/utils/typeHelpers'
import type { BankDetail } from './components/widgets/bank-detail.types'

export interface MerchantReviewTransaction {
  id: number
  date: string
  isoDate: string
  amountInCents: number
  description: string
  details?: BankDetail[]
  account: string
  amount: string
  suggestedCategoryId: number | null
}

export interface MerchantReview {
  groupKey: string
  merchant: string
  position: number
  merchantCount: number
  transactionCount: number
  total: string
  totalInCents: number
  accounts: string[]
  suggestedCategoryIds: number[]
  suggestionReasoning: string | null
  logoUrl: string | null
  plaidCategory: { code: string } | null
  prior: { categoryId: number; confidence: number | null } | null
  transactions: MerchantReviewTransaction[]
}

export interface MerchantReviewState {
  groups: { [groupKey: string]: MerchantReview }
}

export const FETCH_MERCHANT_REVIEW_KEY = 'FETCH_MERCHANT_REVIEW_KEY'

const initialState: MerchantReviewState = {
  groups: {},
}

const merchantReviewSlice = createSlice({
  name: 'merchantReview',
  initialState,
  reducers: {
    receiveMerchantReview: (state, action: PayloadAction<MerchantReview>) => {
      state.groups[action.payload.groupKey] = action.payload
    },
  },
})

export default merchantReviewSlice.reducer

export const { receiveMerchantReview } = merchantReviewSlice.actions

export const MERCHANT_GONE = 'merchant_gone'

const MERCHANT_GONE_ERROR_CODE = 'MERCHANT_NOT_IN_POOL'

const isMerchantGone = (err: unknown): boolean =>
  parseErrorCodeFromCatch(err) === MERCHANT_GONE_ERROR_CODE

export const fetchMerchantReview = (groupKey: string) =>
  fetchWrapper({
    fetchKey: `${FETCH_MERCHANT_REVIEW_KEY}_${groupKey}`,
    defaultErrorMessage: 'There was an error loading this merchant.',
    fetchFunction: async (dispatch) => {
      try {
        const { data } = await apiAxios.get<MerchantReview>(
          `/finances/api/v1/transactions/needing_review/${encodeURIComponent(groupKey)}`
        )
        dispatch(receiveMerchantReview(data))
        return data
      } catch (err) {
        if (isMerchantGone(err)) return MERCHANT_GONE
        throw err
      }
    },
  })

export interface CategorizeResult {
  saved: Array<{ category: string; count: number }>
  personalCount: number
  lockedCount: number
  staleCount: number
  merchantsRemaining: number
}

export const categorizeMerchant = (
  groupKey: string,
  body: {
    assignments: Array<{ transactionId: number; categoryId: number }>
    personalTransactionIds: number[]
  }
) =>
  fetchWrapper({
    fetchKey: `${FETCH_MERCHANT_REVIEW_KEY}_CATEGORIZE_${groupKey}`,
    defaultErrorMessage: 'There was an error saving these categories.',
    fetchFunction: async () => {
      const { data } = await apiAxios.post<CategorizeResult>(
        '/finances/api/v1/transactions/needing_review/categorize',
        body
      )
      return data
    },
  })

export interface HandOffResult {
  transactionCount: number
  lockedCount: number
  merchantsRemaining: number
}

export const handOffMerchant = (groupKey: string, body: { note: string }) =>
  fetchWrapper({
    fetchKey: `${FETCH_MERCHANT_REVIEW_KEY}_HAND_OFF_${groupKey}`,
    defaultErrorMessage: 'There was an error sending this to a bookkeeper.',
    fetchFunction: async () => {
      const { data } = await apiAxios.post<HandOffResult>(
        '/finances/api/v1/transactions/needing_review/hand_off',
        { groupKey, ...body }
      )
      return data
    },
  })

export const markMerchantPersonal = (
  groupKey: string,
  transactionIds: number[]
) =>
  fetchWrapper({
    fetchKey: `${FETCH_MERCHANT_REVIEW_KEY}_MARK_PERSONAL_${groupKey}`,
    defaultErrorMessage: 'There was an error marking this merchant personal.',
    fetchFunction: async () => {
      try {
        const { data } = await apiAxios.post<HandOffResult>(
          '/finances/api/v1/transactions/needing_review/mark_personal',
          { groupKey, transactionIds }
        )
        return data
      } catch (err) {
        if (isMerchantGone(err)) return MERCHANT_GONE
        throw err
      }
    },
  })

export const selectMerchantReview = (
  state: ReduxState,
  groupKey: string
): MerchantReview | undefined => state.merchantReview.groups[groupKey]
